import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, Sparkles, Zap, Crown, Coins } from 'lucide-react';
import toast from 'react-hot-toast';
import { videoStudioService } from '../../services/videoStudioService';
import { CreditPack, CreditPackId, VideoCreditBalance, VideoCreditTransaction } from '../../types/videoStudio';

interface Props {
  open: boolean;
  onClose: () => void;
  balance: VideoCreditBalance | null;
  onPurchased: (b: VideoCreditBalance) => void;
}

const PACK_META: Record<CreditPackId, { icon: any; gradient: string; tagKey?: string }> = {
  starter: { icon: Coins, gradient: 'from-blue-500 to-cyan-500' },
  pro: { icon: Zap, gradient: 'from-violet-500 to-purple-500', tagKey: 'videoStudio.credits.popularTag' },
  studio: { icon: Crown, gradient: 'from-amber-500 to-orange-500', tagKey: 'videoStudio.credits.bestValueTag' }
};

const CreditsModal: React.FC<Props> = ({ open, onClose, balance, onPurchased }) => {
  const { t } = useTranslation();
  const [packs, setPacks] = useState<Record<CreditPackId, CreditPack> | null>(null);
  const [loadingPack, setLoadingPack] = useState<CreditPackId | null>(null);
  const [transactions, setTransactions] = useState<VideoCreditTransaction[]>([]);
  // Custo real de uma cena de 5s no modelo padrão (720p). Era um "/ 10" fixo,
  // calibrado para o Seedance 2.0 — com o 2.5 (7 créditos/s) o pacote de 100
  // créditos prometia "10 cenas" quando rende menos de 3.
  const [sceneCost, setSceneCost] = useState<number>(35);

  useEffect(() => {
    if (!open) return;
    videoStudioService.listCreditPacks().then(r => {
      if (r.success && r.data) setPacks(r.data);
    });
    videoStudioService.estimate({ provider: 'seedance', mode: 'text2video', parameters: { duration: 5, resolution: '720p' } })
      .then(r => { if (r.success && r.data?.cost) setSceneCost(r.data.cost); })
      .catch(() => { /* mantém o padrão */ });
    videoStudioService.getCreditTransactions(8).then(r => {
      if (r.success && r.data) setTransactions(r.data);
    });
  }, [open]);

  if (!open) return null;

  // Real purchase flow: backend creates a Stripe Checkout Session, we redirect
  // the user there. After payment, Stripe sends them back to /video-studio with
  // ?vs_credit_session=cs_xxx — VideoStudioPage handles the confirmation.
  const handleBuy = async (id: CreditPackId) => {
    setLoadingPack(id);
    try {
      const r = await videoStudioService.startCreditCheckout(id);
      if (r.success && r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      throw new Error('Não foi possível iniciar o checkout');
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message || 'Falha ao iniciar checkout');
      setLoadingPack(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/70 dark:bg-gray-950/70 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 rounded-3xl shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl hover:bg-white/5 text-gray-400 hover:text-white transition"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary-500 to-purple-500">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">{t('videoStudio.credits.modalTitle')}</h2>
          </div>
          <p className="text-gray-400 text-sm mb-6">
            <span dangerouslySetInnerHTML={{ __html: t('videoStudio.credits.modalIntro', { count: balance?.available ?? 0 }) }} />
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {(Object.keys(PACK_META) as CreditPackId[]).map((id) => {
              const meta = PACK_META[id];
              const pack = packs?.[id];
              const Icon = meta.icon;
              const isPopular = id === 'pro';
              return (
                <div
                  key={id}
                  className={`relative p-6 rounded-2xl border transition-all ${
                    isPopular
                      ? 'bg-gradient-to-b from-violet-500/10 to-transparent border-violet-500/50 ring-2 ring-violet-500/30'
                      : 'bg-gray-900/50 border-gray-800 hover:border-gray-700'
                  }`}
                >
                  {meta.tagKey && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-gradient-to-r from-primary-500 to-purple-500 text-white">
                      {t(meta.tagKey)}
                    </span>
                  )}
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${meta.gradient} mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-lg capitalize mb-1">{id}</h3>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-3xl font-bold text-white">{pack?.credits ?? '—'}</span>
                    <span className="text-sm text-gray-400">{t('videoStudio.credits.creditsUnit')}</span>
                  </div>
                  <p className="text-2xl font-bold text-white mb-4">
                    R$ {pack?.price_brl ?? '—'}
                    <span className="text-xs text-gray-400 font-normal ml-1">{t('videoStudio.credits.oneTime')}</span>
                  </p>
                  <ul className="space-y-2 mb-6 text-xs text-gray-300">
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      {t('videoStudio.credits.approxScenes', { count: pack ? Math.floor(pack.credits / sceneCost) : 0 })}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      {t('videoStudio.credits.approxPresenters', { count: pack ? Math.floor(pack.credits / 25) : 0 })}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      {t('videoStudio.credits.noExpiration')}
                    </li>
                  </ul>
                  <button
                    onClick={() => handleBuy(id)}
                    disabled={loadingPack === id}
                    className={`w-full py-2.5 rounded-xl font-semibold text-sm transition ${
                      isPopular
                        ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:brightness-110'
                        : 'bg-white/5 text-white border border-gray-700 hover:bg-white/10'
                    } disabled:opacity-50`}
                  >
                    {loadingPack === id ? t('videoStudio.credits.processing') : t('videoStudio.credits.buyNow')}
                  </button>
                </div>
              );
            })}
          </div>

          {transactions.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                {t('videoStudio.credits.lastTransactions')}
              </h3>
              <div className="rounded-2xl border border-gray-800 divide-y divide-gray-800/60 overflow-hidden">
                {transactions.map(tx => (
                  <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-900/40 text-sm">
                    <div>
                      <p className="text-white font-medium">{tx.description || tx.source}</p>
                      <p className="text-xs text-gray-500">{new Date(tx.created_at).toLocaleString('pt-BR')}</p>
                    </div>
                    <span className={`font-bold tabular-nums ${tx.type === 'debit' ? 'text-rose-400' : 'text-emerald-400'}`}>
                      {tx.type === 'debit' ? '−' : '+'}{tx.amount}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditsModal;
